import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { analysisAPI, projectAPI } from '../services/api';
import { Download, FileText, Database, Image, Loader2, Pencil, Check, X, ArrowLeft, XCircle, RefreshCw, ExternalLink, FileSpreadsheet } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Epi2meResults
 *
 * Route: /analysis/:id/results
 *
 * Primary results page for a completed analysis. Shows:
 *   - An interactive plot gallery (NMDS, PCoA, Alpha Diversity, Abundance, Heatmaps)
 *     where each card opens a full-screen iframe modal.
 *   - Downloadable CSV data files.
 *   - Downloadable Bracken/Kraken2 taxonomic reports.
 *   - An inline editor for the project name.
 *
 * Navigation context is read from router location.state so the "back" button
 * returns to wherever the user came from (e.g. BatchAnalysisMonitor or Dashboard).
 *
 * Side effects:
 *   - Fetches result metadata from the API on mount.
 *   - Downloads every PNG thumbnail and HTML plot file as authenticated blob URLs;
 *     all blob URLs are revoked on unmount to free browser memory.
 *   - Registers a global keydown listener while a modal is open (Escape to close).
 */
const Epi2meResults = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Fall back to Dashboard if no navigation context was passed.
  const backTo = location.state?.backTo || '/dashboard';
  const backLabel = location.state?.backLabel || 'Dashboard';
  // Full results object from the API (png_plots, html_plots, csv_files, bracken_reports, etc.).
  const [files, setFiles] = useState(null);
  // True while the initial data fetch is in progress.
  const [loading, setLoading] = useState(true);
  // Error message displayed in the error card.
  const [error, setError] = useState(null);
  // Map of PNG filename → blob URL for thumbnail previews in the gallery.
  const [imageBlobUrls, setImageBlobUrls] = useState({});
  // Map of HTML filename → blob URL for the iframe plots inside the modal.
  const [htmlBlobUrls, setHtmlBlobUrls] = useState({});
  // Transient error message shown when a file download fails (auto-clears after 5 s).
  const [downloadError, setDownloadError] = useState('');

  // Modal state: { plots, title, selectedGrouping } | null
  // null means the modal is closed.
  const [activeModal, setActiveModal] = useState(null);

  // Project name editing state
  // True when the inline name editor is open.
  const [isEditingName, setIsEditingName] = useState(false);
  // Current value of the name input while editing.
  const [editedName, setEditedName] = useState('');
  // True while the PATCH project name request is in flight.
  const [savingName, setSavingName] = useState(false);

  const closeModal = useCallback(() => setActiveModal(null), []);

  // Close modal on Escape — listener is added only while a modal is open.
  useEffect(() => {
    if (!activeModal) return;
    const handleKeyDown = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeModal, closeModal]);

  // Fetch result metadata, then pre-download all PNG thumbnails and HTML plots as
  // blob URLs so they can be embedded in the page without exposing auth tokens in URLs.
  // Cleanup revokes all blob URLs when the component unmounts.
  useEffect(() => {
    let imageBlobUrlsToCleanup = {};
    let htmlBlobUrlsToCleanup = {};

    const fetchData = async () => {
      try {
        const response = await analysisAPI.getEpi2meResults(id);
        setFiles(response.data);

        // Download each PNG thumbnail and store as a blob URL.
        if (response.data.png_plots) {
          const blobUrls = {};
          for (const plot of response.data.png_plots) {
            try {
              const imageResponse = await analysisAPI.downloadResultFile(id, plot.filename);
              const blob = new Blob([imageResponse.data], { type: 'image/png' });
              const url = URL.createObjectURL(blob);
              blobUrls[plot.filename] = url;
            } catch (err) {
              // silently skip missing thumbnails
            }
          }
          imageBlobUrlsToCleanup = blobUrls;
          setImageBlobUrls(blobUrls);
        }

        // Download each HTML interactive plot and store as a blob URL for the iframe.
        if (response.data.html_plots) {
          const htmlBlobs = {};
          for (const html of response.data.html_plots) {
            try {
              const htmlResponse = await analysisAPI.downloadResultFile(id, html.filename);
              const blob = new Blob([htmlResponse.data], { type: 'text/html' });
              const url = URL.createObjectURL(blob);
              htmlBlobs[html.filename] = url;
            } catch (error) {
              // silently skip missing plots
            }
          }
          htmlBlobUrlsToCleanup = htmlBlobs;
          setHtmlBlobUrls(htmlBlobs);
        }
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    // Revoke all blob URLs when this component unmounts to avoid memory leaks.
    return () => {
      Object.values(imageBlobUrlsToCleanup).forEach(url => URL.revokeObjectURL(url));
      Object.values(htmlBlobUrlsToCleanup).forEach(url => URL.revokeObjectURL(url));
    };
  }, [id]);

  /**
   * Downloads a result file by fetching it as a blob and triggering a browser
   * download via a temporary <a> element. This pattern works for files served
   * behind authentication.
   *
   * @param {string} filename - The API-relative filename to download.
   */
  const handleDownload = async (filename) => {
    try {
      const response = await analysisAPI.downloadResultFile(id, filename);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Use only the basename in case filename contains a sub-path.
      link.download = filename.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadError('Failed to download file. Please try again.');
      setTimeout(() => setDownloadError(''), 5000);
    }
  };

  /**
   * Groups a flat list of HTML plot files into named buckets by filename prefix
   * (nmds_, pcoa_, alpha_, abundance_, heatmap10_, heatmap30_).
   * Unrecognised filenames fall into the "other" bucket.
   *
   * @param {Array} plots - Raw plot file objects from the API.
   * @returns {Object} Bucketed arrays keyed by plot type.
   */
  const getGroupedPlots = (plots) => {
    if (!plots) return { nmds: [], pcoa: [], alpha: [], abundance: [], heatmap10: [], heatmap30: [], other: [] };
    const nmds = [], pcoa = [], alpha = [], abundance = [], heatmap10 = [], heatmap30 = [], other = [];

    plots.forEach(p => {
      const filename = p.filename.split('/').pop().replace('.html', '');
      const filenameLower = filename.toLowerCase();

      if (filenameLower.startsWith('nmds_') || filenameLower.startsWith('nmds-')) {
        nmds.push({ ...p, grouping: filename.replace(/^nmds[_-]/i, ''), plotType: 'NMDS' });
      } else if (filenameLower.startsWith('pcoa_') || filenameLower.startsWith('pcoa-')) {
        pcoa.push({ ...p, grouping: filename.replace(/^pcoa[_-]/i, ''), plotType: 'PCoA' });
      } else if (filenameLower.startsWith('alpha_')) {
        alpha.push({ ...p, grouping: filename.replace(/^alpha[_-]/i, ''), plotType: 'Alpha Diversity' });
      } else if (filenameLower.startsWith('abundance_')) {
        abundance.push({ ...p, grouping: filename.replace(/^abundance[_-]/i, ''), plotType: 'Abundance' });
      } else if (filenameLower.startsWith('heatmap10_')) {
        heatmap10.push({ ...p, grouping: filename.replace(/^heatmap10[_-]/i, ''), plotType: 'Heatmap (Top 10)' });
      } else if (filenameLower.startsWith('heatmap30_')) {
        heatmap30.push({ ...p, grouping: filename.replace(/^heatmap30[_-]/i, ''), plotType: 'Heatmap (Top 30)' });
      } else {
        other.push({ ...p, grouping: p.display_name });
      }
    });
    return { nmds, pcoa, alpha, abundance, heatmap10, heatmap30, other };
  };

  /**
   * Converts a byte count into a human-readable file size string.
   * @param {number} bytes
   * @returns {string} e.g. "1.23 MB"
   */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Opens the inline project name editor, pre-filling with the current name.
  const handleEditName = () => {
    setEditedName(files?.project_name || '');
    setIsEditingName(true);
  };

  // Cancels the inline name editor without saving.
  const handleCancelEdit = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  /**
   * Saves the edited project name via PATCH and updates local state on success.
   * Uses downloadError for inline error feedback since there is no dedicated name-error state.
   */
  const handleSaveName = async () => {
    if (!editedName.trim() || !files?.project_id) return;
    setSavingName(true);
    try {
      await projectAPI.update(files.project_id, { name: editedName.trim() });
      setFiles(prev => ({ ...prev, project_name: editedName.trim() }));
      setIsEditingName(false);
    } catch (err) {
      setDownloadError('Failed to update project name');
      setTimeout(() => setDownloadError(''), 3000);
    } finally {
      setSavingName(false);
    }
  };

  /**
   * Downloads every CSV file in the results one by one with a short delay between
   * each to avoid browser popup-blocker suppression of multiple downloads.
   */
  const handleDownloadAllCSVs = async () => {
    if (!files?.csv_files?.length) return;
    for (let i = 0; i < files.csv_files.length; i++) {
      await new Promise(resolve => setTimeout(resolve, i === 0 ? 0 : 300));
      await handleDownload(files.csv_files[i].filename);
    }
  };

  /**
   * Opens the full-screen plot modal for a given plot type group.
   * Defaults to the first plot's grouping as the selected tab.
   *
   * @param {Array} plots - The plots in this group.
   * @param {string} title - Display title for the modal header.
   */
  const openModal = (plots, title) => {
    setActiveModal({ plots, title, selectedGrouping: plots[0]?.grouping });
  };

  /**
   * PlotTypeCard
   *
   * Thumbnail card for a plot type group. Renders a PNG preview (if available)
   * and opens the full-screen iframe modal on click.
   *
   * @param {Array}  plots - All plots in this group (one per grouping/metadata column).
   * @param {string} title - Display name for the card and modal header.
   */
  // Thumbnail card for a plot type group
  const PlotTypeCard = ({ plots, title }) => {
    const firstPlot = plots[0];
    const pngFilename = firstPlot.filename.replace('.html', '.png');
    const thumbnailUrl = imageBlobUrls[pngFilename];

    return (
      <button
        onClick={() => openModal(plots, title)}
        className="group relative rounded-lg border bg-card overflow-hidden text-left hover:shadow-md hover:border-primary/40 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {/* Thumbnail area */}
        <div className="aspect-[4/3] bg-muted/40 relative overflow-hidden">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={title}
              className="w-full h-full object-contain p-3 group-hover:scale-[1.03] transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground/50">
              <Image className="h-10 w-10" />
              <span className="text-xs">Preview loading</span>
            </div>
          )}
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-colors duration-200 flex items-end justify-center pb-3">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-background/90 border text-foreground text-xs px-3 py-1 rounded-full flex items-center gap-1.5 shadow-sm">
              <ExternalLink className="h-3 w-3" />
              Open interactive
            </span>
          </div>
        </div>
        {/* Card footer */}
        <div className="px-3 py-2.5 border-t">
          <span className="font-medium text-sm truncate block">{title}</span>
        </div>
      </button>
    );
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading results...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 max-w-md mx-auto">
        <Card className="border-destructive">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h3 className="font-semibold mb-2">Something went wrong</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button variant="ghost" onClick={() => window.location.reload()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!files) return <div className="p-8">No results found</div>;

  // Build the ordered list of plot groups for the gallery.
  // Each group corresponds to one PlotTypeCard.
  const grouped = files.html_plots ? getGroupedPlots(files.html_plots) : null;
  const plotGroups = grouped
    ? [
        { key: 'nmds',       title: 'NMDS',             plots: grouped.nmds },
        { key: 'pcoa',       title: 'PCoA',             plots: grouped.pcoa },
        { key: 'alpha',      title: 'Alpha Diversity',  plots: grouped.alpha },
        { key: 'abundance',  title: 'Abundance',        plots: grouped.abundance },
        { key: 'heatmap10',  title: 'Heatmap (Top 10)', plots: grouped.heatmap10 },
        { key: 'heatmap30',  title: 'Heatmap (Top 30)', plots: grouped.heatmap30 },
        // Each "other" plot gets its own card.
        ...grouped.other.map(p => ({ key: p.filename, title: p.display_name, plots: [p] })),
      ].filter(g => g.plots.length > 0)
    : [];

  // Currently selected plot inside the modal — resolved by matching selectedGrouping.
  const modalPlot = activeModal
    ? (activeModal.plots.find(p => p.grouping === activeModal.selectedGrouping) || activeModal.plots[0])
    : null;

  return (
    <div className="px-12 py-8 space-y-8">
      {/* Back */}
      <div>
        <Link to={backTo}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to {backLabel}
          </Button>
        </Link>
      </div>

      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold">Results</h1>
          {files?.project_id && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/metadata`)}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Metadata
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Project:</span>
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="px-2 py-1 border rounded-md text-sm bg-background"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') handleCancelEdit();
                }}
              />
              <Button size="sm" variant="ghost" onClick={handleSaveName} disabled={savingName || !editedName.trim()}>
                {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 text-green-600" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancelEdit} disabled={savingName}>
                <X className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="font-medium">{files.project_name || 'Unnamed Project'}</span>
              {files.project_id && (
                <Button size="sm" variant="ghost" onClick={handleEditName} className="h-6 w-6 p-0">
                  <Pencil className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {downloadError && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive p-3 rounded-lg flex items-center gap-2">
          <XCircle className="h-4 w-4 flex-shrink-0" />
          {downloadError}
        </div>
      )}

      {/* Interactive Plots Gallery */}
      {plotGroups.length > 0 && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold">Interactive Plots</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Click any plot to open the interactive view</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            {plotGroups.map(g => (
              <PlotTypeCard key={g.key} plots={g.plots} title={g.title} />
            ))}
          </div>
        </div>
      )}

      {/* CSV Data Files */}
      {files.csv_files && files.csv_files.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Data Files
            </CardTitle>
            <Button size="sm" variant="outline" onClick={handleDownloadAllCSVs}>
              <Download className="h-4 w-4 mr-1.5" />
              Download All
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {files.csv_files.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-primary" />
                    <div>
                      <p className="font-medium text-sm">{file.display_name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleDownload(file.filename)}>
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bracken Reports */}
      {files.bracken_reports && files.bracken_reports.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Bracken Taxonomic Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 max-h-96 overflow-y-auto">
              {files.bracken_reports.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-2 min-w-0">
                    <Database className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-mono text-xs truncate">{file.display_name}</p>
                      <p className="text-xs text-muted-foreground">Kraken2 Report • {formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleDownload(file.filename)} className="flex-shrink-0">
                    <Download className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plot Modal — full-screen iframe viewer with a grouping selector dropdown */}
      <AnimatePresence>
        {activeModal && (
          <motion.div
            key="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal} />

            {/* Dialog */}
            <motion.div
              key="modal-dialog"
              initial={{ opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="relative w-full bg-background flex flex-col"
              style={{ width: '100vw', height: '100vh' }}
            >
              {/* Modal header */}
              <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                  <h2 className="font-semibold text-base">{activeModal.title}</h2>
                  {/* Grouping selector — shown when a plot type has multiple sub-groupings */}
                  {activeModal.plots.length > 1 && (
                    <select
                      value={activeModal.selectedGrouping}
                      onChange={(e) => setActiveModal(prev => ({ ...prev, selectedGrouping: e.target.value }))}
                      className="px-2 py-1 border rounded text-sm bg-background"
                    >
                      {activeModal.plots.map(p => (
                        <option key={p.grouping} value={p.grouping}>
                          {p.grouping.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownload(modalPlot?.filename.replace('.html', '.png'))}
                  >
                    <Download className="h-4 w-4 mr-1.5" />
                    PNG
                  </Button>
                  <Button size="sm" variant="ghost" onClick={closeModal} aria-label="Close">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Modal body — iframe renders the interactive HTML plot via blob URL */}
              <div className="flex-1 overflow-hidden min-h-0">
                {htmlBlobUrls[modalPlot?.filename] ? (
                  <iframe
                    key={modalPlot.filename}
                    src={htmlBlobUrls[modalPlot.filename]}
                    title={activeModal.title}
                    className="w-full h-full border-0"
                    style={{ minHeight: '0', height: '100%' }}
                    sandbox="allow-scripts allow-same-origin"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mr-2" />
                    <span className="text-muted-foreground text-sm">Loading interactive plot...</span>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Epi2meResults;
